import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  type MouseEvent,
  type ReactNode,
  useId,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { subscribeToDismissFloatingLayers } from "../_shared/dismissFloatingLayers"
import { DSBadge } from "../DSBadge"
import { cn } from "../../utils/cn"
import { buildDataId, slugifyDataId } from "../../utils/dataId"
import { getFieldShellClassName, getFieldShellErrorStyle } from "../../utils/fieldShell"
import { resolveSelectPanelStyle } from "./selectUtils"

type DsSelectOption = {
  disabled?: boolean
  leftAdornment?: ReactNode
  label: string
  tooltip?: string
  trailingLabel?: string
  value: string
}

type DsSelectValue = string | readonly string[] | undefined

type DsSelectProps = {
  "data-id"?: string
  className?: string
  defaultOpen?: boolean
  defaultValue?: DsSelectValue
  disabled?: boolean
  emptyHint?: string
  errorMessage?: string
  leftIcon?: ReactNode
  multiple?: boolean
  onChange?: (value: string | string[]) => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  optionClassName?: string
  options: DsSelectOption[]
  placeholder: string
  selectedSummary?: string
  variant?: "custom" | "default"
  value?: DsSelectValue
} & Omit<ComponentPropsWithoutRef<"div">, "children" | "className" | "onChange">

function normalizeSingleValue(value: DsSelectValue) {
  if (Array.isArray(value)) {
    return value[0] ?? ""
  }

  return value ?? ""
}

function normalizeMultiValue(value: DsSelectValue) {
  if (Array.isArray(value)) {
    return [...value]
  }

  if (value === undefined || value === "") {
    return []
  }

  return [value]
}

function setSelectRefValue(
  ref: ForwardedRef<HTMLButtonElement> | undefined,
  value: HTMLButtonElement | null
) {
  if (!ref) {
    return
  }

  if (typeof ref === "function") {
    ref(value)
    return
  }

  ref.current = value
}

function SelectMultiIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex size-5 shrink-0 items-center justify-center rounded-sm border transition-colors duration-150 ease-out",
        checked ? "border-brand-primary-3 bg-brand-primary-3" : "border-gray-5 bg-body-light-mode-1"
      )}
    >
      <svg
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute size-3 text-body-light-mode-1 transition-opacity duration-150 ease-out",
          checked ? "opacity-100" : "opacity-0"
        )}
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="M2.5 6.25 4.75 8.5 9.5 3.75"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    </span>
  )
}

type OverlayStyle = {
  left: number
  top: number
  width: number
}

const DsSelect = forwardRef<HTMLButtonElement, DsSelectProps>(function DsSelect(
  {
    className,
    defaultOpen = false,
    defaultValue,
    disabled = false,
    emptyHint,
    errorMessage,
    leftIcon,
    multiple = false,
    onChange,
    onOpenChange,
    open,
    optionClassName,
    options,
    placeholder,
    selectedSummary,
    variant = "default",
    value,
    "data-id": dataIdProp,
    ...props
  },
  forwardedRef
) {
  const generatedId = useId()
  const listId = `${generatedId}-list`
  const errorId = errorMessage ? `${generatedId}-error` : undefined
  const dataId = dataIdProp ?? buildDataId("select", slugifyDataId(placeholder))
  const rootProps = { ...props, "data-id": dataId }
  const rootRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const [uncontrolledValue, setUncontrolledValue] = useState<DsSelectValue>(defaultValue)
  const [panelStyle, setPanelStyle] = useState<OverlayStyle | null>(null)
  const [optionTooltip, setOptionTooltip] = useState<{
    left: number
    text: string
    top: number
  } | null>(null)

  const isOpen = open ?? uncontrolledOpen
  const currentValue = value ?? uncontrolledValue
  const selectedValues = multiple ? normalizeMultiValue(currentValue) : [normalizeSingleValue(currentValue)].filter(Boolean)
  const selectedOptionMap = new Map(options.map(option => [option.value, option]))
  const selectedLabels = selectedValues
    .map(selectedValue => selectedOptionMap.get(selectedValue)?.label ?? selectedValue)
    .filter(Boolean)
  const selectedOption = multiple ? null : selectedOptionMap.get(selectedValues[0] ?? "")
  const selectedLabelsKey = selectedLabels.join("|")
  const hasSelectedValue = selectedLabels.length > 0
  const selectedCountLabel = multiple
    ? selectedSummary ?? `${selectedLabels.length} opções adicionadas`
    : selectedSummary ?? selectedLabels[0] ?? ""
  const isCustomVariant = variant === "custom"

  function setButtonRef(node: HTMLButtonElement | null) {
    setSelectRefValue(forwardedRef, node)
  }

  function commitOpen(nextOpen: boolean) {
    if (open === undefined) {
      setUncontrolledOpen(nextOpen)
    }

    if (!nextOpen) {
      setOptionTooltip(null)
    }

    onOpenChange?.(nextOpen)
  }

  function showOptionTooltip(event: MouseEvent<HTMLElement>, tooltip: string) {
    const rect = event.currentTarget.getBoundingClientRect()

    setOptionTooltip({
      left: rect.left + rect.width / 2,
      text: tooltip,
      top: rect.top - 8,
    })
  }

  function hideOptionTooltip() {
    setOptionTooltip(null)
  }

  function commitValue(nextValue: string | string[]) {
    if (value === undefined) {
      setUncontrolledValue(nextValue)
    }

    onChange?.(nextValue)
  }

  function toggleOption(option: DsSelectOption) {
    if (disabled || option.disabled) {
      return
    }

    if (multiple) {
      const nextValues = selectedValues.includes(option.value)
        ? selectedValues.filter(selectedValue => selectedValue !== option.value)
        : [...selectedValues, option.value]
      commitValue(nextValues)
      return
    }

    commitValue(option.value)
    commitOpen(false)
  }

  function handleTriggerClick() {
    if (disabled) {
      return
    }

    commitOpen(!isOpen)
  }

  function handleBadgeRemove(valueToRemove: string) {
    if (disabled) {
      return
    }

    commitValue(selectedValues.filter(selectedValue => selectedValue !== valueToRemove))
  }

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle(null)
      return
    }

    const updatePanelPosition = () => {
      setPanelStyle(
        resolveSelectPanelStyle(rootRef.current, {
          optionCount: options.length,
          panel: panelRef.current,
          variant,
        })
      )
    }

    updatePanelPosition()
    window.addEventListener("resize", updatePanelPosition)
    window.addEventListener("scroll", updatePanelPosition, true)

    return () => {
      window.removeEventListener("resize", updatePanelPosition)
      window.removeEventListener("scroll", updatePanelPosition, true)
    }
  }, [isOpen, options.length, selectedLabelsKey, variant])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        commitOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null

      if (!target) {
        return
      }

      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return
      }

      commitOpen(false)
    }

    document.addEventListener("pointerdown", handlePointerDown)

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    return subscribeToDismissFloatingLayers(() => {
      commitOpen(false)
    })
  }, [isOpen])

  const hasError = Boolean(errorMessage)

  return (
    <div
      className={cn("relative flex flex-col gap-2 font-inter", className)}
      data-slot="select-root"
      ref={rootRef}
      {...rootProps}
    >
      <div
        className={cn(
          isCustomVariant ? "bg-body-light-mode-1" : "bg-light-gray",
          hasError ? "shadow-none ring-1 ring-feedback-danger/40" : "shadow-none",
          getFieldShellClassName({ hasError, isActive: isOpen })
        )}
        data-slot="select-field"
        style={getFieldShellErrorStyle(hasError)}
      >
        <button
          aria-describedby={errorId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-invalid={hasError || undefined}
          aria-controls={listId}
          className={cn(
            isCustomVariant
              ? "flex min-h-14 w-full items-center gap-2 bg-transparent px-4 py-2 text-left"
              : "flex min-h-14 w-full items-center gap-3 bg-transparent px-4 py-0 text-left",
            "border-0 shadow-none outline-none transition-colors duration-200 ease-out",
            disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
          )}
          data-id={buildDataId(dataId, "trigger")}
          data-slot="select-trigger"
          disabled={disabled}
          onClick={handleTriggerClick}
          ref={setButtonRef}
          type="button"
        >
        {leftIcon ? (
          <span className="flex size-5 shrink-0 items-center justify-center text-gray-5" data-slot="select-left-icon">
            {leftIcon}
          </span>
        ) : null}

        <span
          className={cn(
            "flex min-w-0 flex-1 flex-col text-[14px] leading-[normal] tracking-[0px]",
            isCustomVariant
              ? hasSelectedValue
                ? "justify-center gap-2"
                : "justify-center"
              : hasSelectedValue
                ? "justify-center gap-1 py-2"
                : "justify-center py-0"
          )}
          data-slot="select-content"
        >
          <span
            className={cn(
              "truncate font-semibold text-gray-5",
              isCustomVariant ? "text-[14px]" : "",
              hasSelectedValue ? "text-gray-5" : "text-gray-5"
            )}
            data-slot="select-placeholder"
          >
            {placeholder}
          </span>

          {!hasSelectedValue && emptyHint ? (
            <span
              className="truncate font-semibold text-[14px] text-gray-5"
              data-slot="select-empty-hint"
            >
              {emptyHint}
            </span>
          ) : null}

          {hasSelectedValue ? (
            isCustomVariant && !multiple && selectedOption?.leftAdornment ? (
              <span
                className="flex w-full min-w-0 items-start gap-2"
                data-slot="select-selected-value"
              >
                <span
                  className="flex h-[15px] w-[21px] shrink-0 items-center justify-center overflow-hidden"
                  data-slot="select-selected-left-adornment"
                >
                  {selectedOption.leftAdornment}
                </span>
                <span className="min-w-0 whitespace-normal break-words font-normal text-[14px] text-dark-3">
                  {selectedCountLabel}
                </span>
              </span>
            ) : (
              <span
                className={cn(
                  "min-w-0 whitespace-normal break-words font-normal text-dark-3",
                  isCustomVariant ? "text-[14px] text-dark-3" : "",
                  multiple ? "text-gray-5" : "text-dark-3"
                )}
                data-slot="select-selected-value"
              >
                {selectedCountLabel}
              </span>
            )
          ) : null}
        </span>

        <span className="flex shrink-0 items-center justify-center text-gray-5" data-slot="select-arrow">
          <svg aria-hidden="true" fill="none" height="20" viewBox="0 0 20 20" width="20">
            <path
              d="m4.5 7.5 5.5 5.5 5.5-5.5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
            />
          </svg>
        </span>
      </button>
      </div>

      {multiple && hasSelectedValue ? (
        <div className="flex flex-wrap gap-2" data-slot="select-badges">
          {selectedLabels.map((label, index) => {
            const selectedValue = selectedValues[index]

            return (
              <button
                aria-label={`Remover ${label}`}
                className="group"
                data-id={buildDataId(dataId, "badge-remove", selectedValue)}
                disabled={disabled}
                key={selectedValue}
                onClick={() => handleBadgeRemove(selectedValue)}
                type="button"
              >
                <DSBadge className="flex h-[22px] min-h-[22px] min-w-0 items-center gap-1 px-2 py-0 text-[12px] font-normal leading-[normal] tracking-[0px]">
                  <span>{label}</span>
                  <span aria-hidden className="text-[14px] leading-none cursor-pointer">
                    ×
                  </span>
                </DSBadge>
              </button>
            )
          })}
        </div>
      ) : null}

      {isOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              aria-multiselectable={multiple || undefined}
              className={cn(
                "fixed z-[61] max-h-[240px] overflow-y-auto overflow-x-hidden",
                "scrollbar-select rounded-[16px] bg-body-light-mode-1 shadow-[0_12px_30px_rgba(16,24,40,0.12)]"
              )}
              data-id={buildDataId(dataId, "list")}
              data-slot="select-list"
              id={listId}
              ref={panelRef}
              role="listbox"
              style={
                panelStyle
                  ? {
                      left: `${panelStyle.left}px`,
                      top: `${panelStyle.top}px`,
                      width: `${panelStyle.width}px`,
                    }
                  : undefined
              }
            >
              {options.map(option => {
                const isSelected = multiple
                  ? selectedValues.includes(option.value)
                  : normalizeSingleValue(currentValue) === option.value
                const isOptionDisabled = disabled || Boolean(option.disabled)
                const optionButton = (
                  <button
                    aria-selected={isSelected}
                    className={cn(
                      isCustomVariant
                        ? "flex min-h-12 w-full items-start gap-3 px-4 py-3 text-left text-[14px] leading-[normal] tracking-[0px] transition-colors duration-150 ease-out"
                        : "flex min-h-10 w-full items-start gap-3 px-4 py-2 text-left text-[14px] leading-[normal] tracking-[0px] transition-colors duration-150 ease-out",
                      multiple
                        ? "bg-body-light-mode-1 font-normal text-dark-3 hover:bg-gray-2"
                        : isSelected
                          ? isCustomVariant
                            ? "bg-brand-primary-3-20 text-dark-3"
                            : "bg-brand-primary-3-20  text-dark-3"
                          : "bg-body-light-mode-1 font-normal text-dark-3 hover:bg-gray-2",
                      isOptionDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                      option.tooltip && isOptionDisabled ? "pointer-events-none" : undefined,
                      optionClassName
                    )}
                    data-id={buildDataId(dataId, "option", option.value)}
                    data-slot="select-option"
                    disabled={isOptionDisabled}
                    onClick={() => toggleOption(option)}
                    role="option"
                    type="button"
                    >
                    {multiple ? <SelectMultiIndicator checked={isSelected} /> : null}
                    {!multiple && option.leftAdornment ? (
                      <span className="flex size-5 shrink-0 items-center justify-center pt-0.5 text-gray-5">
                        {option.leftAdornment}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1 whitespace-normal break-words">
                      {option.label}
                    </span>
                    {option.trailingLabel ? (
                      <span
                        className={cn(
                          "shrink-0 self-center text-[12px] font-bold leading-[normal] tracking-[0px]",
                          option.disabled ? "text-gray-5" : "text-feedback-success"
                        )}
                        data-slot="select-option-trailing"
                      >
                        {option.trailingLabel}
                      </span>
                    ) : null}
                  </button>
                )

                return (
                  <span
                    className="relative block w-full"
                    data-slot={option.tooltip ? "select-option-tooltip-anchor" : undefined}
                    key={option.value}
                    onMouseEnter={
                      option.tooltip
                        ? event => showOptionTooltip(event, option.tooltip ?? "")
                        : undefined
                    }
                    onMouseLeave={option.tooltip ? hideOptionTooltip : undefined}
                  >
                    {optionButton}
                  </span>
                )
              })}
            </div>,
            document.body
          )
        : null}

      {optionTooltip && typeof document !== "undefined"
        ? createPortal(
            <span
              className="pointer-events-none fixed z-[62] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-brand-primary-3 px-3 py-1 text-[14px] font-normal leading-[normal] tracking-[0px] text-body-light-mode-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
              data-slot="select-option-tooltip"
              role="tooltip"
              style={{ left: optionTooltip.left, top: optionTooltip.top }}
            >
              {optionTooltip.text}
            </span>,
            document.body
          )
        : null}

      {errorMessage ? (
        <p
          className="text-left text-[12px] font-normal leading-[normal] tracking-[0px] text-feedback-danger"
          data-slot="select-error"
          id={errorId}
        >
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
})

DsSelect.displayName = "DsSelect"

export { DsSelect }
export type { DsSelectOption, DsSelectProps }
